<template>
  <div class="filter-panel">
    <div class="filter-panel__toolbar">
      <div class="filter-panel__left">
        <el-badge v-if="$slots.advanced" :hidden="activeCount === 0" :value="activeCount">
          <el-tooltip :content="filtersExpanded ? 'Hide filters' : 'More filters'" placement="top">
            <el-button
                :aria-expanded="filtersExpanded"
                circle
                class="filter-panel__toggle"
                :icon="filtersExpanded ? 'ArrowUp' : 'ArrowDown'"
                plain
                size="small"
                type="primary"
                @click="toggleFilters"
            />
          </el-tooltip>
        </el-badge>

        <div class="filter-panel__primary">
          <slot name="primary"/>
        </div>
      </div>

      <div v-if="$slots.actions" class="filter-panel__actions">
        <slot name="actions"/>
      </div>
    </div>

    <el-collapse-transition>
      <div v-if="!isMobile && filtersExpanded" class="filter-panel__advanced">
        <div class="filter-panel__advanced-fields">
          <slot name="advanced"/>
        </div>
      </div>
    </el-collapse-transition>

    <el-drawer
        v-if="isMobile"
        :model-value="filtersExpanded"
        direction="rtl"
        size="min(360px, 92vw)"
        title="Additional filters"
        @update:model-value="setFiltersExpanded"
    >
      <div class="filter-panel__drawer-fields">
        <slot name="advanced"/>
      </div>
    </el-drawer>
  </div>
</template>

<script lang="ts">
import {Options, Vue} from "vue-class-component";

@Options({
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Boolean,
      default: undefined
    },
    activeCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isMobile: false,
      internalExpanded: false
    }
  },
  computed: {
    filtersExpanded() {
      return this.modelValue === undefined ? this.internalExpanded : this.modelValue;
    }
  },
  mounted() {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateViewport);
  },
  methods: {
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
    },
    toggleFilters() {
      this.setFiltersExpanded(!this.filtersExpanded);
    },
    setFiltersExpanded(expanded: boolean) {
      if (this.modelValue === undefined) {
        this.internalExpanded = expanded;
      }
      this.$emit('update:modelValue', expanded);
    }
  }
})
export default class SearchFilterPanel extends Vue {
}
</script>

<style scoped>
.filter-panel {
  width: 100%;
  margin-bottom: 12px;
}

.filter-panel__toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-panel__left {
  display: flex;
  flex: 1 1 auto;
  gap: 10px;
  align-items: center;
  min-width: 0;
}

.filter-panel__left > .el-badge {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
}

:deep(.filter-panel__toggle.el-button) {
  width: 36px;
  min-width: 36px;
  height: 36px;
  min-height: 36px;
  padding: 0;
  border-radius: 50%;
}

.filter-panel__primary {
  display: flex;
  flex: 1 1 auto;
  gap: 10px;
  align-items: center;
  min-width: 0;
  padding: 5px;
  overflow-x: auto;
  scrollbar-width: none;
}

.filter-panel__primary::-webkit-scrollbar {
  display: none;
}

.filter-panel__primary :deep(.search-field) {
  flex: 0 0 auto;
  margin-right: 0;
  margin-bottom: 0;
}

.filter-panel__primary :deep(:where(
  .search-field > .el-input,
  .search-field > .el-select,
  .search-field > .el-date-editor,
  .search-field > .el-input-number
)) {
  width: var(--filter-field-width, 200px);
}

.filter-panel__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
}

.filter-panel__advanced {
  margin-top: 8px;
  padding: 10px 12px 5px;
  background: linear-gradient(180deg, #f8fbff 0%, #f1f8ff 100%);
  border: 1px solid var(--color-brand-surface-2);
  border-radius: 12px;
}

.filter-panel__advanced-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 10px;
  align-items: flex-start;
}

.filter-panel__advanced-fields :deep(.search-field) {
  margin-right: 0;
  margin-bottom: 5px;
}

.filter-panel__drawer-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-panel__drawer-fields :deep(.search-field),
.filter-panel__drawer-fields :deep(.el-select),
.filter-panel__drawer-fields :deep(.el-input),
.filter-panel__drawer-fields :deep(.el-input-number) {
  width: 100% !important;
  margin: 0;
}

@media (max-width: 768px) {
  .filter-panel__toolbar {
    align-items: center;
  }

  .filter-panel__left {
    min-width: 0;
  }

  .filter-panel__actions {
    max-width: 42vw;
    margin-top: -10px;
    padding-top: 10px;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .filter-panel__actions::-webkit-scrollbar {
    display: none;
  }
}
</style>
